import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  confirmPhoneVerification,
  createDisposition,
  deactivateDisposition,
  getAgents,
  getCall,
  getCallingMe,
  getCallLog,
  getDispositions,
  getProviderConfig,
  getRecordCalls,
  linkCallToRecord,
  reorderDispositions,
  rotateWebhookToken,
  saveProviderConfig,
  setAgentPhone,
  setCallDisposition,
  startPhoneVerification,
  testProviderConnection,
  updateDisposition,
  updateMyPhone,
  verifyAgent,
} from '@/services/api/calling.service'
import { useAuthStore } from '@/stores/auth.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type {
  AgentPhoneRequest,
  Call,
  CallLogFilters,
  DispositionRequest,
  ProviderConfigRequest,
  SetDispositionRequest,
} from '@/types/api/calling'

/** Query keys live under the workspace so switching workspaces never shows another workspace's calls. */
export const callingKeys = {
  all: (ws: string | null) => ['workspace', ws, 'call'] as const,
  call: (ws: string | null, callId: string) => ['workspace', ws, 'call', callId] as const,
  record: (ws: string | null, recordId: string, page: number) => ['workspace', ws, 'call', 'record', recordId, page] as const,
  log: (ws: string | null, filters: CallLogFilters, page: number, size: number) =>
    ['workspace', ws, 'call', 'log', filters, page, size] as const,
  me: (ws: string | null) => ['workspace', ws, 'calling', 'me'] as const,
  config: (ws: string | null) => ['workspace', ws, 'calling', 'config'] as const,
  dispositions: (ws: string | null, includeInactive: boolean) =>
    ['workspace', ws, 'calling', 'dispositions', includeInactive] as const,
  agents: (ws: string | null) => ['workspace', ws, 'calling', 'agents'] as const,
}

function useWorkspaceId() {
  return useWorkspaceStore((s) => s.currentWorkspaceId)
}

/** What the current user can do with calling (feature, permissions, modes, phone, devices). */
export function useCallingMe() {
  const ws = useWorkspaceId()
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: callingKeys.me(ws),
    queryFn: getCallingMe,
    enabled: !!accessToken,
    staleTime: 60 * 1000,
  })
}

/**
 * Whether calling is available here: switched on for the deployment and in the workspace's plan.
 * False while loading, so calling UI never flashes in before the server answers.
 */
export function useCallingEnabled() {
  const { data, isLoading } = useCallingMe()
  return { enabled: !!data?.featureEnabled, isLoading }
}

/**
 * One call. While it is live and the realtime stream is down, poll every 3 s; the stream otherwise
 * keeps the cache fresh (see useCallRealtimeSync).
 */
export function useCall(callId: string | null | undefined, options: { pollWhileLive?: boolean } = {}) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: callingKeys.call(ws, callId ?? ''),
    queryFn: () => getCall(callId!),
    enabled: !!callId,
    staleTime: 5 * 1000,
    refetchInterval: (query) => {
      const call = query.state.data as Call | undefined
      return options.pollWhileLive && call?.status !== 'ENDED' ? 3_000 : false
    },
  })
}

export function useRecordCalls(recordId: string | undefined, page = 0, enabled = true) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: callingKeys.record(ws, recordId ?? '', page),
    queryFn: () => getRecordCalls(recordId!, page),
    enabled: !!recordId && enabled,
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
  })
}

export function useCallLog(filters: CallLogFilters, page: number, size = 25) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: callingKeys.log(ws, filters, page, size),
    queryFn: () => getCallLog(filters, page, size),
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
  })
}

export function useSetDisposition() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  return useMutation({
    mutationFn: ({ callId, data }: { callId: string; data: SetDispositionRequest }) => setCallDisposition(callId, data),
    onSuccess: (call) => {
      queryClient.setQueryData(callingKeys.call(ws, call.id), call)
      void queryClient.invalidateQueries({ queryKey: callingKeys.all(ws) })
      if (call.recordId) {
        // The CALL timeline entry and the record's follow-up changed too.
        void queryClient.invalidateQueries({ queryKey: ['activities', call.recordId] })
      }
    },
  })
}

export function useLinkCall() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  return useMutation({
    mutationFn: ({ callId, recordId }: { callId: string; recordId: string }) => linkCallToRecord(callId, recordId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callingKeys.all(ws) })
    },
  })
}

function useMeMutation<T>(fn: (arg: T) => Promise<unknown>) {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      queryClient.setQueryData(callingKeys.me(ws), data)
    },
  })
}

export function useUpdateMyPhone() {
  return useMeMutation((data: AgentPhoneRequest) => updateMyPhone(data))
}

export function useStartPhoneVerification() {
  return useMeMutation(() => startPhoneVerification())
}

export function useConfirmPhoneVerification() {
  return useMeMutation(() => confirmPhoneVerification())
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export function useProviderConfig(enabled = true) {
  const ws = useWorkspaceId()
  return useQuery({ queryKey: callingKeys.config(ws), queryFn: getProviderConfig, enabled })
}

export function useSaveProviderConfig() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  return useMutation({
    mutationFn: (data: ProviderConfigRequest) => saveProviderConfig(data),
    onSuccess: (config) => {
      queryClient.setQueryData(callingKeys.config(ws), { ...config, newWebhook: null })
      void queryClient.invalidateQueries({ queryKey: callingKeys.me(ws) })
      void queryClient.invalidateQueries({ queryKey: ['workspace', ws, 'calling', 'dispositions'] })
    },
  })
}

export function useTestProviderConnection() {
  return useMutation({ mutationFn: testProviderConnection })
}

export function useRotateWebhookToken() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  return useMutation({
    mutationFn: rotateWebhookToken,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: callingKeys.config(ws) })
    },
  })
}

export function useDispositions(includeInactive = false, enabled = true) {
  const ws = useWorkspaceId()
  return useQuery({
    queryKey: callingKeys.dispositions(ws, includeInactive),
    queryFn: () => getDispositions(includeInactive),
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useDispositionMutations() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['workspace', ws, 'calling', 'dispositions'] })
  return {
    create: useMutation({ mutationFn: (data: DispositionRequest) => createDisposition(data), onSuccess: invalidate }),
    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: DispositionRequest }) => updateDisposition(id, data),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({ mutationFn: (id: string) => deactivateDisposition(id), onSuccess: invalidate }),
    reorder: useMutation({ mutationFn: (ids: string[]) => reorderDispositions(ids), onSuccess: invalidate }),
  }
}

export function useAgents(enabled = true) {
  const ws = useWorkspaceId()
  return useQuery({ queryKey: callingKeys.agents(ws), queryFn: getAgents, enabled })
}

export function useAgentMutations() {
  const queryClient = useQueryClient()
  const ws = useWorkspaceId()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: callingKeys.agents(ws) })
  return {
    setPhone: useMutation({
      mutationFn: ({ userId, data }: { userId: string; data: AgentPhoneRequest }) => setAgentPhone(userId, data),
      onSuccess: invalidate,
    }),
    verify: useMutation({ mutationFn: (userId: string) => verifyAgent(userId), onSuccess: invalidate }),
  }
}
